import {
  ChartBar,
  DownloadCloud,
  History,
  ListChecks,
  RefreshCw,
  Settings,
  Shield,
  Swords,
  TrendingUp,
  Trophy,
  Umbrella,
  UploadCloud,
  Users,
} from "lucide-solid";
import { For, Show, createMemo, createSignal } from "solid-js";
import { REPORT_STATUS_VARIANT, type PendingTierChange, type Report } from "system";
import { TierManagerDialog } from "~/components/admin-tier-manager";
import { PageContainer, PageHeader } from "~/components/layout/page";
import {
  CHECKLIST_KEY,
  ChecklistItem,
  changeId,
  loadChecked,
} from "~/components/pending-changes-button";
import { Avatar } from "~/components/ui/avatar";
import { Badge } from "~/components/ui/badge";
import { Button, buttonVariants } from "~/components/ui/button";
import { Card, CardHeader, CardTitle } from "~/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { VoucherManagerDialog } from "~/components/voucher-manager";
import { adminApi, playersApi, reportsApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { requireFeature } from "~/lib/features";
import { QueryKey } from "~/lib/query-keys";
import { TIER_COLORS, cn, formatDateTime, timeAgo } from "~/lib/utils";

import {
  createMutation,
  createQuery,
  useQueryClient,
} from "@tanstack/solid-query";
import { Link } from "@tanstack/solid-router";
import { createFileRoute } from "@tanstack/solid-router";

export const Route = createFileRoute("/_authed/admin/")({
  beforeLoad: () => requireFeature("admin"),
  component: AdminPage,
});

function AdminPage() {
  const { state } = useAuth();
  const isJudge = () =>
    state.user?.role === "judge" || state.user?.role === "admin";
  const isAdmin = () => state.user?.role === "admin";

  return (
    <Show when={isJudge()} fallback={<RestrictedNotice />}>
      <PageContainer>
        <PageHeader
          title={isAdmin() ? "Admin Panel" : "Reports"}
          description={
            isAdmin()
              ? "Manage the racing group"
              : "Dispute reports submitted by drivers"
          }
        >
          <Show when={isAdmin()}>
            <AdminActions />
          </Show>
        </PageHeader>

        {/* One grid, rendered once. Desktop follows source order; on mobile the
            Reports cell jumps to the top so the actionable queue leads. */}
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PendingTierChangesCard />
          <div class="order-first lg:order-none">
            <ReportsSection />
          </div>
          <Show when={isAdmin()}>
            <SystemModeCard />
          </Show>
          <Show when={isJudge()}>
            <MembersCard />
          </Show>
          <Show when={isAdmin()}>
            <AnalyticsCard />
          </Show>
          <Show when={isAdmin()}>
            <SyncHistoryCard />
          </Show>
        </div>
      </PageContainer>
    </Show>
  );
}

function RestrictedNotice() {
  return (
    <PageContainer>
      <div class="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <Shield size={40} class="text-text-faint" />
        <h2 class="font-display text-xl font-semibold text-text-base">
          Restricted
        </h2>
        <p class="text-sm text-text-muted max-w-xs">
          Only judges and admins can access this panel.
        </p>
      </div>
    </PageContainer>
  );
}

// ── Reports ──────────────────────────────────────────────────────────────────

function ReportsSection() {
  const reports = createQuery(() => ({
    queryKey: [QueryKey.Reports, "all"],
    queryFn: () => reportsApi.list(),
  }));

  // Open work (needs a judge) is split from the closed history, so the actionable
  // cases are not buried under resolved ones.
  const inProgress = () =>
    (reports.data ?? []).filter(
      (r) =>
        r.status === "open" ||
        r.status === "under_review" ||
        r.status === "escalated",
    );
  const history = () =>
    (reports.data ?? []).filter(
      (r) => r.status === "resolved" || r.status === "dismissed",
    );

  return (
    <Show
      when={reports.data}
      fallback={<div class="h-20 bg-surface-2 rounded-xl animate-pulse" />}
    >
      <div class="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Open reports</CardTitle>
          </CardHeader>
          <Show
            when={inProgress().length > 0}
            fallback={
              <p class="text-center text-text-faint py-8">
                Nothing needs attention.
              </p>
            }
          >
            <For each={inProgress()}>
              {(report) => <ReportRow report={report} />}
            </For>
          </Show>
        </Card>

        <Show when={history().length > 0}>
          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <For each={history()}>
              {(report) => <ReportRow report={report} />}
            </For>
          </Card>
        </Show>
      </div>
    </Show>
  );
}

function ReportRow(props: { report: Report }) {
  const r = () => props.report;

  // Two lines so the issue text starts at a fixed left edge instead of drifting
  // behind the variable-width status badge. The whole row is a stretched link to the
  // report detail; the submitter and duel links ride above it (z-10), no nested <a>.
  return (
    <div class="relative -mx-2 rounded-md px-2 py-2 transition-colors hover:bg-surface-3">
      <Link
        to="/reports/$reportId"
        params={{ reportId: r().id }}
        aria-label="Open report"
        class="absolute inset-0 rounded-md"
      />
      {/* Line 1: status, who filed it, when. */}
      <div class="flex items-center gap-2 text-xs">
        <Badge variant={REPORT_STATUS_VARIANT[r().status]}>
          {r().status.replace("_", " ")}
        </Badge>
        <Show when={r().reporter}>
          <Link
            to="/profile/$username"
            params={{ username: r().reporter!.username }}
            search={{ tab: "duels" }}
            class="relative z-10 font-medium text-text-muted transition-colors hover:text-brand"
          >
            @{r().reporter!.username}
          </Link>
        </Show>
        <span class="ml-auto shrink-0 text-text-faint">
          {timeAgo(r().createdAt)}
        </span>
      </div>
      {/* Line 2: the issue (quoted), and the duel it concerns. */}
      <div class="mt-0.5 flex items-baseline gap-3">
        <p class="min-w-0 flex-1 truncate text-sm text-text-base">
          <span class="text-text-faint">›</span> {r().issue}
        </p>
        <Show when={r().duel}>
          <Link
            to="/duels/$duelId"
            params={{ duelId: r().duel!.id }}
            class="relative z-10 inline-flex shrink-0 items-center gap-1 text-xs text-brand hover:underline"
          >
            <Swords size={10} />
            {r().duel?.attacker?.displayName ?? "?"} vs{" "}
            {r().duel?.defender?.displayName ?? "?"}
          </Link>
        </Show>
      </div>
    </div>
  );
}

// ── Admin tools ──────────────────────────────────────────────────────────────

function AdminActions() {
  const qc = useQueryClient();
  const [result, setResult] = createSignal<string | null>(null);
  const [confirmOpen, setConfirmOpen] = createSignal(false);
  const [uploadOpen, setUploadOpen] = createSignal(false);

  // In master mode the app is authoritative and the sheet is a downstream export,
  // so pulling from the sheet is a recovery/restore action, not a routine sync.
  const config = createQuery(() => ({
    queryKey: [QueryKey.Admin, "config"],
    queryFn: () => adminApi.config(),
  }));
  const master = () => !!config.data?.masterModeActive;

  const sync = createMutation(() => ({
    mutationFn: () => adminApi.sync(),
    onSuccess: (data) => {
      setResult(`Sync complete. ${data.rowsAffected} rows updated.`);
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: [QueryKey.Rankings] });
      qc.invalidateQueries({ queryKey: [QueryKey.Admin, "sync-history"] });
      qc.invalidateQueries({
        queryKey: [QueryKey.Admin, "pending-tier-changes"],
      });
    },
    onError: (e: any) => {
      setResult(`Error: ${e.message}`);
      setConfirmOpen(false);
    },
  }));

  const upload = createMutation(() => ({
    mutationFn: () => adminApi.uploadToSheet(),
    onSuccess: (data) => {
      setResult(
        data.skipped
          ? "Sheets not configured — nothing uploaded."
          : `Uploaded ${data.written} cells to the sheet.`,
      );
      setUploadOpen(false);
    },
    onError: (e: any) => {
      setResult(`Error: ${e.message}`);
      setUploadOpen(false);
    },
  }));

  return (
    <div class="flex flex-wrap items-center gap-2 pt-1">
      <Link
        to="/duels"
        search={{ tab: "disputed" }}
        class={cn(
          buttonVariants({ variant: "secondary", size: "sm" }),
          "gap-1.5",
        )}
      >
        <Swords size={13} />
        Disputed duels
      </Link>
      <Show when={master()}>
        <Dialog open={uploadOpen()} onOpenChange={setUploadOpen}>
          <DialogTrigger
            as={Button}
            variant="secondary"
            size="sm"
            class="gap-1.5"
            disabled={upload.isPending}
          >
            <UploadCloud size={13} />
            Upload all ranks to Sheet
          </DialogTrigger>
          <DialogContent title="Upload Rankings to Sheet">
            <div class="space-y-4">
              <p class="text-sm text-text-muted">
                Overwrites every tier cell in the Google Sheet with the app's
                current rankings. Use this to (re)populate the read-only export
                from the authoritative app state — for example, the first time
                you enable master mode. It does not change app rankings.
              </p>
              <div class="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setUploadOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => upload.mutate()}
                  disabled={upload.isPending}
                  class="gap-1.5"
                >
                  <UploadCloud
                    size={13}
                    class={upload.isPending ? "animate-pulse" : ""}
                  />
                  {upload.isPending ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </Show>
      {/* Sheet → DB pull is sheet-authoritative and disabled in master mode (the
          route refuses it too). To restore from the sheet, an admin turns master
          mode off first — that flip is its own confirmation. */}
      <Show
        when={!master()}
        fallback={
          <p class="text-xs text-text-faint max-w-md">
            Sheet sync is disabled in master mode - the app is the source of
            truth. To restore rankings from the sheet (recovery only), turn
            master mode off first, then run the sync.
          </p>
        }
      >
        <Dialog open={confirmOpen()} onOpenChange={setConfirmOpen}>
          <DialogTrigger
            as={Button}
            variant="danger-outline"
            size="sm"
            class="gap-1.5"
            disabled={sync.isPending}
          >
            <DownloadCloud size={13} />
            Sync Rankings
          </DialogTrigger>
          <DialogContent title="Confirm Rankings Sync">
            <div class="space-y-4">
              <p class="text-sm text-text-muted">
                Rankings are pulled directly from Google Sheets. If a judge is
                mid-update, uncommitted changes will be silently overwritten
                with no recovery path.
              </p>
              <div class="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => sync.mutate()}
                  disabled={sync.isPending}
                  class="gap-1.5"
                >
                  <RefreshCw
                    size={13}
                    class={sync.isPending ? "animate-spin" : ""}
                  />
                  {sync.isPending ? "Syncing..." : "Sync Anyway"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </Show>
      <Show when={result()}>
        <p class="text-xs text-text-muted">{result()}</p>
      </Show>
    </div>
  );
}

function AnalyticsCard() {
  const analytics = createQuery(() => ({
    queryKey: [QueryKey.Admin, "analytics"],
    queryFn: () => adminApi.analytics(),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analytics</CardTitle>
        <ChartBar size={16} class="text-text-faint" />
      </CardHeader>
      <Show when={analytics.data} fallback={<Skeleton />}>
        {(a) => (
          <div class="grid grid-cols-2 gap-3">
            <Stat label="Total Drivers" value={a().totalPlayers} />
            <Stat label="Total Duels" value={a().totalDuels} />
            <Stat label="Active Duels" value={a().activeDuels} />
            <Stat label="Disputed" value={a().disputedDuels} />
            <Stat label="Completed" value={a().completedDuels} />
            <Stat label="Total Matches" value={a().totalMatches} />
          </div>
        )}
      </Show>
    </Card>
  );
}

const ENABLE_BULLETS = [
  "Ranking changes apply immediately on duel and tournament completion.",
  "The admin review queue is retired — no manual approval needed.",
  "Sync will no longer overwrite app-applied tiers.",
  "Google Sheets will receive automatic write-backs after each result.",
];

const DISABLE_BULLETS = [
  "Ranking changes return to the pending review queue.",
  "Past direct writes remain in place — this does not roll back history.",
  "Sheets will stop receiving automatic write-backs.",
  "Google Sheets resumes as the authoritative ranking source.",
];

function SystemModeCard() {
  const qc = useQueryClient();
  const config = createQuery(() => ({
    queryKey: [QueryKey.Admin, "config"],
    queryFn: () => adminApi.config(),
  }));

  const [step, setStep] = createSignal<0 | 1 | 2>(0);
  const [phrase, setPhrase] = createSignal("");

  const close = () => {
    setStep(0);
    setPhrase("");
  };

  const toggle = createMutation(() => ({
    mutationFn: () => adminApi.setMasterMode(!config.data!.masterModeActive),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QueryKey.Admin, "config"] });
      close();
    },
    onError: close,
  }));

  return (
    <Show when={config.data?.masterModeEnabled}>
      <Card>
        <CardHeader>
          <CardTitle>Ranking Mode</CardTitle>
          <Settings size={16} class="text-text-faint" />
        </CardHeader>
        <Show when={config.data} fallback={<Skeleton />}>
          {(cfg) => {
            const enabling = () => !cfg().masterModeActive;
            const label = () => (enabling() ? "Enable" : "Disable");
            const expectedPhrase = () =>
              enabling() ? "ENABLE MASTER MODE" : "DISABLE MASTER MODE";
            return (
              <div class="flex flex-col gap-3">
                <div class="flex items-center gap-2">
                  <span
                    class={cn(
                      "inline-block w-2 h-2 rounded-full",
                      cfg().masterModeActive ? "bg-green-400" : "bg-amber-400",
                    )}
                  />
                  <span class="text-sm text-text-base font-medium">
                    {cfg().masterModeActive
                      ? "App-authoritative"
                      : "Sheet-authoritative"}
                  </span>
                </div>
                <p class="text-xs text-text-faint">
                  {cfg().masterModeActive
                    ? "Ranking changes are applied immediately on duel and tournament completion."
                    : "Ranking changes are queued for manual review and applied via Google Sheets sync."}
                </p>
                <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                  {label()} Master Mode
                </Button>
                <Dialog
                  open={step() > 0}
                  onOpenChange={(o) => {
                    if (!o) close();
                  }}
                >
                  <DialogContent
                    title={step() === 2 ? "Confirm" : `${label()} Master Mode`}
                  >
                    <Show when={step() === 1}>
                      <div class="space-y-4">
                        <p class="text-sm text-text-muted">
                          This will change how rankings are managed:
                        </p>
                        <ul class="space-y-1.5 text-sm text-text-muted list-disc pl-4">
                          <For
                            each={enabling() ? ENABLE_BULLETS : DISABLE_BULLETS}
                          >
                            {(b) => <li>{b}</li>}
                          </For>
                        </ul>
                        <div class="flex justify-end gap-2">
                          <Button variant="outline" onClick={close}>
                            Cancel
                          </Button>
                          <Button onClick={() => setStep(2)}>Continue</Button>
                        </div>
                      </div>
                    </Show>
                    <Show when={step() === 2}>
                      <div class="space-y-4">
                        <p class="text-sm text-text-muted">
                          Type{" "}
                          <span class="font-mono font-semibold text-text-base">
                            {expectedPhrase()}
                          </span>{" "}
                          to confirm.
                        </p>
                        <Input
                          value={phrase()}
                          onInput={(e) => setPhrase(e.currentTarget.value)}
                          placeholder={expectedPhrase()}
                        />
                        <div class="flex justify-end gap-2">
                          <Button variant="outline" onClick={close}>
                            Cancel
                          </Button>
                          <Button
                            onClick={() => toggle.mutate()}
                            disabled={
                              phrase() !== expectedPhrase() || toggle.isPending
                            }
                          >
                            {toggle.isPending ? "Applying…" : "Confirm"}
                          </Button>
                        </div>
                      </div>
                    </Show>
                  </DialogContent>
                </Dialog>
              </div>
            );
          }}
        </Show>
      </Card>
    </Show>
  );
}

function PendingTierChangesCard() {
  const config = createQuery(() => ({
    queryKey: [QueryKey.Admin, "config"],
    queryFn: () => adminApi.config(),
  }));
  const changes = createQuery(() => ({
    queryKey: [QueryKey.Admin, "pending-tier-changes"],
    queryFn: () => adminApi.pendingTierChanges(),
    // The card is hidden in master mode (apply-then-review handles changes), so
    // don't fetch the queue there. Only query once we know we're sheet-authoritative.
    enabled: config.data !== undefined && !config.data.masterModeActive,
  }));
  const [checked, setChecked] = createSignal<Set<string>>(loadChecked());
  const [open, setOpen] = createSignal(false);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(CHECKLIST_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const clearChecked = () => {
    setChecked(new Set<string>());
    localStorage.removeItem(CHECKLIST_KEY);
  };

  const total = () => changes.data?.length ?? 0;
  const uncheckedCount = createMemo(
    () =>
      (changes.data ?? []).filter((c) => !checked().has(changeId(c))).length,
  );
  const uncheckedItems = createMemo(() =>
    (changes.data ?? []).filter((c) => !checked().has(changeId(c))),
  );

  // Hidden entirely in master mode: changes apply directly (apply-then-review),
  // so there is nothing to manually reconcile. The review inbox will reclaim this
  // slot (PLAN 7.9). Until config loads we render nothing rather than flash.
  return (
    <Show when={config.data && !config.data.masterModeActive}>
      <Card>
        <CardHeader>
          <CardTitle>Pending Tier Changes</CardTitle>
          <div class="flex items-center gap-2 ml-auto mt-1">
            <Show when={!config.data?.masterModeActive && total() > 0}>
              <span
                class={cn(
                  "text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap",
                  uncheckedCount() === 0
                    ? "bg-green-500/20 text-green-400"
                    : "bg-amber-500/20 text-amber-400",
                )}
              >
                {uncheckedCount() === 0
                  ? "All clear"
                  : `${uncheckedCount()} remaining`}
              </span>
            </Show>
            <TrendingUp size={16} class="text-text-faint" />
          </div>
        </CardHeader>
        <Show when={!config.data?.masterModeActive}>
          <Show when={changes.data} fallback={<Skeleton />}>
            <Show
              when={total() > 0}
              fallback={
                <p class="text-xs text-text-faint py-4 text-center">
                  No pending changes since last sync.
                </p>
              }
            >
              <div class="space-y-0 mb-3">
                <For each={uncheckedItems().slice(0, 3)}>
                  {(change) => <CompactChangePreview change={change} />}
                </For>
                <Show when={uncheckedCount() > 3}>
                  <p class="text-xs text-text-faint text-center pt-1.5">
                    +{uncheckedCount() - 3} more
                  </p>
                </Show>
              </div>
              <Dialog open={open()} onOpenChange={setOpen}>
                <DialogTrigger
                  as={Button}
                  variant="outline"
                  class="w-full gap-2 text-xs"
                >
                  <ListChecks size={14} />
                  Open Checklist ({total() - uncheckedCount()} / {total()} done)
                </DialogTrigger>
                <DialogContent title="Sheets Update Checklist">
                  <div class="space-y-4">
                    <div class="flex items-center justify-between gap-4">
                      <p class="text-xs text-text-faint">
                        Check off each row after updating Google Sheets.
                      </p>
                      <Show when={checked().size > 0}>
                        <button
                          onClick={clearChecked}
                          class="text-xs text-text-faint hover:text-text-muted underline underline-offset-2 shrink-0"
                        >
                          Clear checked
                        </button>
                      </Show>
                    </div>
                    <div class="space-y-2 max-h-[60vh] overflow-y-auto pr-0.5">
                      <For each={changes.data ?? []}>
                        {(change) => (
                          <ChecklistItem
                            change={change}
                            checked={checked().has(changeId(change))}
                            onToggle={() => toggle(changeId(change))}
                          />
                        )}
                      </For>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </Show>
          </Show>
        </Show>
      </Card>
    </Show>
  );
}

function CompactChangePreview(props: { change: PendingTierChange }) {
  return (
    <div class="flex items-center gap-2 text-xs py-1.5 border-b border-border-subtle last:border-0">
      <Show
        when={props.change.source === "duel" && props.change}
        fallback={
          <Show when={props.change.source === "tournament" && props.change}>
            {(t) => (
              <>
                <Trophy size={11} class="text-text-faint shrink-0" />
                <span class="text-text-muted truncate flex-1">
                  {t().tournamentName}
                </span>
                <span class="text-text-faint text-label-sm shrink-0">
                  {t().promoted.length} promoted
                </span>
              </>
            )}
          </Show>
        }
      >
        {(d) => (
          <>
            <TrendingUp size={11} class="text-green-400 shrink-0" />
            <span class="text-text-base font-medium truncate flex-1">
              {d().winner.displayName}
            </span>
            <span class="flex items-center gap-0.5 text-label-sm shrink-0">
              <span class={TIER_COLORS[d().winner.fromTier].text}>
                {capitalize(d().winner.fromTier)}
              </span>
              <span class="text-text-faint mx-0.5">→</span>
              <span class={TIER_COLORS[d().winner.toTier].text}>
                {capitalize(d().winner.toTier)}
              </span>
            </span>
          </>
        )}
      </Show>
    </div>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function SyncHistoryCard() {
  const history = createQuery(() => ({
    queryKey: [QueryKey.Admin, "sync-history"],
    queryFn: () => adminApi.syncHistory(),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sync History</CardTitle>
        <History size={16} class="text-text-faint" />
      </CardHeader>
      <Show when={history.data} fallback={<Skeleton />}>
        <div class="space-y-2 max-h-48 overflow-y-auto">
          <For each={history.data}>
            {(entry: any) => (
              <div class="flex items-center justify-between text-xs py-1.5 border-b border-border-subtle">
                <span class="text-text-faint">
                  {formatDateTime(entry.syncedAt)}
                </span>
                <span class="text-text-muted">{entry.rowsAffected} rows</span>
                <Badge
                  variant={entry.status === "success" ? "success" : "danger"}
                >
                  {entry.status}
                </Badge>
              </div>
            )}
          </For>
          <Show when={!history.data?.length}>
            <p class="text-xs text-text-faint py-4 text-center">
              No sync history.
            </p>
          </Show>
        </div>
      </Show>
    </Card>
  );
}

function MembersCard() {
  const { state } = useAuth();
  const isAdmin = () => state.user?.role === "admin";
  const [filter, setFilter] = createSignal("");
  // Selected driver for the tier-management dialog (admin + master mode only).
  const [tierUserId, setTierUserId] = createSignal<string | null>(null);
  // Selected driver for the voucher-management dialog (judges + admins).
  const [voucherUserId, setVoucherUserId] = createSignal<string | null>(null);

  const drivers = createQuery(() => ({
    queryKey: [QueryKey.Players, "all"],
    queryFn: () => playersApi.list(),
  }));

  const config = createQuery(() => ({
    queryKey: [QueryKey.Admin, "config"],
    queryFn: () => adminApi.config(),
  }));
  const masterMode = () => !!config.data?.masterModeActive;

  const filtered = () => {
    const q = filter().toLowerCase();
    return (drivers.data ?? []).filter(
      (p: any) =>
        !q ||
        p.username?.toLowerCase().includes(q) ||
        p.displayName?.toLowerCase().includes(q),
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
        <Users size={16} class="text-text-faint" />
      </CardHeader>
      <Show when={drivers.data} fallback={<Skeleton />}>
        <Input
          placeholder="Filter by username…"
          value={filter()}
          onInput={(e) => setFilter(e.currentTarget.value)}
          class="mb-3"
        />
        <p class="text-xs text-text-faint mb-2">
          Choose a member to manage rankings{" "}
          <ChartBar class="inline" size={10} /> or vacations{" "}
          <Umbrella class="inline" size={10} /> for.
        </p>
        <div class="space-y-2 max-h-72 overflow-y-auto p-4 -mx-4">
          <For each={filtered()}>
            {(player: any) => (
              <div class="flex items-center gap-2 py-1.5">
                <Link
                  to="/profile/$username"
                  params={{ username: player.username }}
                  search={{ tab: "progression" }}
                  class="flex items-center gap-2 flex-1 min-w-0 rounded-md -mx-1 px-1 py-1 hover:bg-surface-3 transition-colors"
                >
                  <Avatar
                    src={player.avatarUrl ?? undefined}
                    fallback={player.displayName}
                    size="sm"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-text-base truncate border-b border-b-solid border-b-text-faint">
                      {player.displayName}
                    </p>
                    <p class="text-xs text-text-faint truncate">
                      @{player.username}
                    </p>
                  </div>
                </Link>
                <Show when={player.role !== "member"}>
                  <Badge
                    variant={player.role === "admin" ? "brand" : "warning"}
                  >
                    {player.role}
                  </Badge>
                </Show>
                {/* Until better times */}
                {/* <Show when={isAdmin() && masterMode()}>
                  <Button
                    variant="outline"
                    title="Manage tiers"
                    onClick={() => setTierUserId(player.id)}
                    class="pressable rounded text-text-white hover:text-brand"
                  >
                    <ChartBar size={14} />
                  </Button>
                </Show> */}
                <Button
                  variant="outline"
                  title="Manage vacation vouchers"
                  onClick={() => setVoucherUserId(player.id)}
                  class="pressable rounded text-text-faint hover:text-brand"
                >
                  <Umbrella size={14} />
                </Button>
              </div>
            )}
          </For>
        </div>
      </Show>
      {/* Until better times */}
      {/* <TierManagerDialog
        userId={tierUserId()}
        onClose={() => setTierUserId(null)}
      /> */}
      <VoucherManagerDialog
        userId={voucherUserId()}
        onClose={() => setVoucherUserId(null)}
      />
    </Card>
  );
}

function Stat(props: { label: string; value: number }) {
  return (
    <div class="bg-surface-3 rounded-lg px-3 py-2.5">
      <p class="font-display text-xl font-bold text-text-base">{props.value}</p>
      <p class="text-xs text-text-faint">{props.label}</p>
    </div>
  );
}

function Skeleton() {
  return <div class="h-20 rounded-lg bg-surface-3 animate-pulse" />;
}
